import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaTag } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const CreateTag = () => {
  const { projectId } = useProjectPhone();
  const [form, setForm] = useState({ tagName: "", category: "" });
  const [existingTags, setExistingTags] = useState([]);
  const [tagCount, setTagCount] = useState(0);
  const [maxTags, setMaxTags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [tagsRes, projectRes] = await Promise.all([
        apihubGet(
          `/getInteractedCustomerDetail/fetchtagsbyprojectid/${projectId}`,
        ),
        apihubGet(`/getInteractedCustomerDetail/projects/${projectId}`),
      ]);
      const list = Array.isArray(tagsRes?.data?.data)
        ? tagsRes.data.data
        : [];
      setExistingTags(list);
      setTagCount(list.length);
      const project = projectRes?.project || projectRes?.data?.project || {};
      const limit =
        project?.noOfTags ??
        project?.features?.noOfTags ??
        null;
      setMaxTags(typeof limit === "number" ? limit : null);
    } catch (err) {
      setError(err.message || "Failed to load tag settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => {
    const set = new Set();
    existingTags.forEach((t) => {
      if (t.category && t.category !== "-") set.add(t.category);
    });
    return [...set].sort();
  }, [existingTags]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setMessage("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) return;
    if (!form.tagName.trim()) {
      setError("Tag name is required.");
      return;
    }
    if (maxTags != null && tagCount >= maxTags) {
      setError("Please upgrade your plan to add more tags.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await apihubPost("/getInteractedCustomerDetail/createtags", {
        tagName: form.tagName.trim(),
        category: form.category.trim(),
        projectId,
      });
      setMessage("Tag created successfully.");
      setForm({ tagName: "", category: "" });
      load();
    } catch (err) {
      setError(err.message || "Failed to create tag");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-stack">
        <div>
          <h1>
            <FaTag />
            Create Tags
          </h1>
          <p className="biz-panel-subtitle">
            Pick a name and category that describes your contacts.
          </p>
        </div>
        <Link
          to={businessPath(projectId, "fetchalltags")}
          className="biz-btn-secondary"
        >
          <FaArrowLeft />
          Go Back
        </Link>
      </div>

      {loading && <p className="biz-table-message">Loading…</p>}
      {error && <p className="biz-form-error">{error}</p>}
      {message && <p className="biz-form-success">{message}</p>}

      {!loading && (
        <form className="biz-tags-create-form" onSubmit={onSubmit}>
          <label className="biz-form-field">
            <span>Tag Name</span>
            <p className="biz-field-hint">
              Pick something that describes your contact.
            </p>
            <input
              type="text"
              name="tagName"
              value={form.tagName}
              onChange={onChange}
              placeholder="Enter tag name"
              required
            />
          </label>

          <label className="biz-form-field">
            <span>Category</span>
            <p className="biz-field-hint">
              Pick something that describes your contact.
            </p>
            <select
              name="category"
              value={form.category}
              onChange={onChange}
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <span className="biz-field-hint">or create new</span>
            <input
              type="text"
              name="category"
              value={form.category}
              onChange={onChange}
              placeholder="Enter category"
            />
          </label>

          {maxTags != null && (
            <p className="biz-field-hint">
              Tags used: {tagCount} / {maxTags}
            </p>
          )}

          <button
            type="submit"
            className="biz-btn-primary"
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </form>
      )}
    </div>
  );
};

export default CreateTag;
